import React, { useState, useEffect, Fragment } from "react";
import { AlertUnsavedData, Modal, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import { useDirtyData } from 'hooks/useDirtyData';
import { FormMultiSelect, FormTextInput } from 'components/form-fields';
import { useValidation } from "hooks/useValidation";
import { Button, ButtonSave } from "components/buttons";
import { FleetService } from "services/FleetService";
import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { CheckService } from "services/CheckService";

export function SupplierModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = true,
        onSave,
        onCloseModal,
        supplierDetails,
        existingSupplierNames = []
    }
) {
    const defaultSupplierModel = {
        id: 0,
        name: "",
        categoryId: 1,
        relatedCosts: []
    };

    const assetCategoryName = "asset";

    const [costTypes, setCostTypes] = useState([]);
    const [assetCategory, setAssetCategory] = useState([]);
    const [internalLoading, setInternalLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalSupplier, setOriginalSupplier] = useState(defaultSupplierModel);
    const [supplier, setSupplier] = useState(originalSupplier);
    const isSupplierDirty = useDirtyData(originalSupplier, supplier);
    const [isSupplierValid, supplierErrors, validateSupplier] = useValidation();
    const [blockModalClose, setBlockModalClose] = useState(false);

    useEffect(loadCategories, []);

    function loadCategories() {
        if (!showModal || !internalLoading) {
            return;
        }

        const costRequest = {
            requestCount: 1000,
            pageNumber: 1,
            searchTerm: null,
            filters: [{ key: "CategoryName", value: assetCategoryName }]
        };

        setInternalLoading(true);

        axios.all([
            FleetService.getCosts(costRequest),
            CheckService.getTemplateOptions()
        ])
        .then(axios.spread((costTypesResponse, templateOptionsResponse) => {
            setCostTypes(costTypesResponse.data);

            var assetCat = templateOptionsResponse.templateCategories.find(c => c.name.toLowerCase() === assetCategoryName.toLowerCase());

            setAssetCategory(assetCat);
        }))
        .finally(() => {
            setInternalLoading(false);
        });
    }

    useEffect(loadDetails, [supplierDetails]);

    function loadDetails() {
        if (!showModal || !supplierDetails) {
            return;
        }

        const mappedSupplierDetails = {
            id: supplierDetails.id || defaultSupplierModel.id,
            name: supplierDetails.name || defaultSupplierModel.name,
            categoryId: 1,
            relatedCosts: supplierDetails.relatedCosts || []
        };

        setOriginalSupplier(mappedSupplierDetails);
        setSupplier(mappedSupplierDetails);
    }

    function onSupplierFieldChange(fieldName, passedValue) {
        setSupplier({
            ...supplier,
            [fieldName]: passedValue
        });
    }

    function onCostTypesChange(fieldName, item) {
        setSupplier((prev) => {
            var tempList = [...prev.relatedCosts];
            
            const updatedSupplierCosts = {
                ...prev
            };

            if (tempList.some(c => c.id === item.id)) {
                const index = tempList.findIndex(c => c.id === item.id);
                tempList.splice(index, 1);

                updatedSupplierCosts[fieldName] =  tempList;

                return updatedSupplierCosts;
            }

            tempList.push(item);

            updatedSupplierCosts[fieldName] =  tempList;

            return updatedSupplierCosts;
        });
    }

    function onCostTypeRemove(fieldName, _, index) {
        setSupplier((prev) => {
            var tempList = [...prev[fieldName]];
            tempList.splice(index, 1);

            return {
                ...prev,
                [fieldName]: tempList
            };
        });
    }

    useEffect(validate, [
        supplier,
        isSupplierDirty,
        isSupplierValid,
        loading
    ]);

    function validate() {
        if (!showModal || loading) {
            return; 
        }

        let rules = [
            { fieldName: "name", required: true, maxLength: 100, notInCollection: { collection: existingSupplierNames, originalValue: originalSupplier.name } }
        ];

        validateSupplier(rules, supplier);
    }

    function onSaveClick() {
        if (!isUpdate && 
            (
                !isSupplierValid || 
                !isSupplierDirty
            )
        ) {
            return;
        }

        const supplierRequest = {
            name: supplier.name,
            categoryId: assetCategory.id,
            costTypeIds: supplier.relatedCosts.map(c => c.id)
        };

        setSaving(true);

        if (isUpdate) {
            FleetService.updateSupplier(supplier.id, supplierRequest)
                .then(() => {
                    NotificationToaster.show(Intent.SUCCESS, "Supplier updated successfully.");
                    onSaveComplete();
                    resetModal();
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Could not update supplier. Please try again.");
                })
                .finally(() => {
                    setSaving(false);
                });

            return;
        }

        FleetService.createSupplier(supplierRequest)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Supplier created successfully.");
                onSaveComplete();
                resetModal();
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not create supplier. Please try again.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isSupplierDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginalSupplier(defaultSupplierModel);
        setSupplier(defaultSupplierModel);
    }
    
    return (
        <Fragment>
            <Modal 
                isOpen={showModal}
                onClose={onCloseClick}
                clickOutsideClose={true}
                title={modalTitle}
                isCloseButtonShown={false}
                updateStateOnClose={false}
            >
                <FormTextInput
                    id="input-supplier-name"
                    disabled={loading}
                    loading={loading}
                    value={supplier.name}
                    headingText="Supplier Name"
                    placeholder="Please type the supplier name here"
                    onChange={(event) => onSupplierFieldChange("name", event.target.value)}
                    dangerHelperText={supplierErrors.name}
                    large={true}
                    fill={true}
                />

                <FormMultiSelect
                    loading={loading}
                    disabled={saving}
                    items={costTypes}
                    onItemSelect={(item) => onCostTypesChange("relatedCosts", item)}
                    onRemove={(item, index) => onCostTypeRemove("relatedCosts", item, index)}
                    placeholder="Please select"
                    headingText="Select cost types"
                    dangerHelperText={supplierErrors.costTypes}
                    selectedValues={supplier?.relatedCosts ? supplier?.relatedCosts : []}
                    fill={true}
                />

                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent="none"
                            onClick={() => onCloseClick(false)}
                        />
                        <ButtonSave
                            id="add-asset-type-sub-type-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isSupplierValid || loading || !isSupplierDirty}
                            text={'Save'}
                        />
                    </div>
                </div>
            </Modal>

            <AlertUnsavedData
                isDirty={isSupplierDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

SupplierModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    supplierDetails: PropTypes.object.isRequired,
    existingSupplierNames: PropTypes.array
};